import React, { useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useQueryStates, parseAsInteger, parseAsStringEnum } from 'nuqs'
import { useUserPermissionsQuery } from 'queries/admin/userPermissions'
import {
  ALL_USER_PERMISSIONS,
  canManageCompanyScopes,
  getUIPermission,
  getUserPermissionOptions,
  UserPermission,
  UserPermissionOption,
} from 'api/admin/userPermissions/permissions'
import { useWhoamiContextValue } from 'components/Whoami/WhoamiProvider'
import { PrivilegeUser } from 'api/admin/userPermissions/types'

const permissionFilterParser = parseAsStringEnum(
  Array.from(ALL_USER_PERMISSIONS)
)

type UserPermissionsContextState = {
  state: {
    items: PrivilegeUser[]
    dataUpdatedAt: number
    isPending: boolean
    isFetching: boolean
    isFetchingNextPage: boolean
    hasNextPage: boolean
    isError: boolean
    error: string
    userId: number | null
    permission: UserPermission | null
    manageablePermissionOptions: UserPermissionOption[]
    canManageCompanyScopes: boolean
  }
  actions: {
    refetch: () => void
    loadMore: () => void
    setUserId: (value: number | null) => void
    setPermission: (value: UserPermission | null) => void
    clearFilters: () => void
  }
}

const [Context, useUserPermissionsContextValue] =
  createContext<UserPermissionsContextState>({
    name: 'UserPermissionsContext',
  })

export const UserPermissionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useQueryStates({
    userId: parseAsInteger,
    permission: permissionFilterParser,
  })

  const { hasPermission } = useWhoamiContextValue()
  const uiPermission = getUIPermission(hasPermission)
  const manageablePermissionOptions = useMemo(
    () => (uiPermission != null ? getUserPermissionOptions(uiPermission) : []),
    [uiPermission]
  )

  const setUserId = (value: number | null) => {
    setFilters({ userId: value })
  }

  const setPermission = (value: UserPermission | null) => {
    setFilters({ permission: value })
  }

  const clearFilters = () => {
    setFilters({ userId: null, permission: null })
  }

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useUserPermissionsQuery({
    userId: filters.userId ?? undefined,
    permission: filters.permission ?? undefined,
    pageSize: 50,
  })

  const items = data?.pages.flatMap((page) => page.users) ?? []

  const value: UserPermissionsContextState = {
    state: {
      items,
      dataUpdatedAt,
      isPending,
      isFetching,
      isFetchingNextPage,
      hasNextPage: Boolean(hasNextPage),
      isError,
      error: parseErrorMessage(error),
      userId: filters.userId,
      permission: filters.permission,
      manageablePermissionOptions,
      canManageCompanyScopes: canManageCompanyScopes(uiPermission),
    },
    actions: {
      refetch,
      loadMore: () => {
        void fetchNextPage()
      },
      setUserId,
      setPermission,
      clearFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useUserPermissionsContextValue }
